import PropTypes from 'prop-types';
import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Col, Form, Input, Row,
} from 'antd';
import { mdiClose, mdiContentSaveOutline, mdiLockOutline } from '@mdi/js';

import InfoBlock from './InfoBlock/InfoBlock';
import AntButtonWithMDI from './AntButtonWithMDI';
import { ApiContext } from '../api/ApiContextProvider';
import {
  antNotification, capitalize, validateNoEmoji, validateNotOnlySpaces,
} from '../mainUtils';

const passwordRules = [
  { required: true },
  { min: 4 },
  { max: 200 },
  { validator: validateNotOnlySpaces },
  { validator: validateNoEmoji },
];

const oldPasswordRules = [{ required: true }, { min: 4 }, { max: 200 }];
const confirmRules = [{ required: true }, { min: 4 }, { max: 200 }];

function ChangePasswordComponent({ onCancel }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { requestUpdateUserPassword } = useContext(ApiContext);

  const validateMessages = useMemo(() => ({
    required: capitalize(t('auth.validation.messages.required', 'is required')),
    string: {
      // eslint-disable-next-line no-template-curly-in-string
      min: capitalize(t('auth.validation.messages.min', 'must be at least ${min} characters')),
      // eslint-disable-next-line no-template-curly-in-string
      max: capitalize(t('auth.validation.messages.max', 'cannot be longer than ${max} characters')),
    },
  }), [t]);

  const onSubmit = useCallback(async () => {
    try {
      await form.validateFields();
      const { old_password, password, password_confirmation } = form.getFieldsValue();

      if (password !== password_confirmation) {
        antNotification.error(capitalize(t(
          'auth.validation.messages.passwords_not_match',
          'password and password confirmation do not match',
        )));
        return;
      }

      requestUpdateUserPassword({ old_password, password, password_confirmation })
        .then(() => {
          antNotification.success(capitalize(t(
            'auth.notifications.updated_successfully',
            'password successfully updated',
          )));
          form.resetFields();
          onCancel?.();
        })
        .catch(() => {
          antNotification.error(capitalize(t('auth.notifications.error', 'error')));
        });
    } catch {
      antNotification.error(capitalize(t('auth.notifications.invalid_data', 'invalid data entered')));
    }
  }, [form, t, requestUpdateUserPassword, onCancel]);

  return (
    <Form
      layout="vertical"
      autoComplete="off"
      form={form}
      validateMessages={validateMessages}
    >
      <Row className="mb-2">
        <Col span={24} className="flex">
          <AntButtonWithMDI
            className="button-primary ml-auto"
            size="small"
            pathForMdi={mdiContentSaveOutline}
            onClick={onSubmit}
            label={capitalize(t('auth.buttons.save', 'save'))}
          />
          <AntButtonWithMDI
            className="ml-2 button-secondary-outlined"
            size="small"
            onClick={onCancel}
            pathForMdi={mdiClose}
            label={capitalize(t('auth.buttons.cancel', 'cancel'))}
          />
        </Col>
      </Row>
      <InfoBlock
        iconPath={mdiLockOutline}
        title={capitalize(t('auth.headers.change_password', 'change password'))}
        className="bg-blue"
      >
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              label={capitalize(t('auth.headers.old_password', 'old password'))}
              name="old_password"
              rules={oldPasswordRules}
            >
              <Input.Password
                autoComplete="current-password"
                placeholder={capitalize(t('auth.placeholders.enter_old_password', 'enter old password'))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={capitalize(t('auth.headers.new_password', 'new password'))}
              name="password"
              rules={passwordRules}
            >
              <Input.Password
                autoComplete="new-password"
                placeholder={capitalize(t('auth.placeholders.enter_new_password', 'enter new password'))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={capitalize(t('auth.headers.password_confirmation', 'password confirmation'))}
              name="password_confirmation"
              rules={confirmRules}
            >
              <Input.Password
                autoComplete="new-password"
                placeholder={capitalize(t('auth.placeholders.confirm_password', 'confirm new password'))}
              />
            </Form.Item>
          </Col>
        </Row>
      </InfoBlock>
    </Form>
  );
}

ChangePasswordComponent.propTypes = {
  onCancel: PropTypes.func,
};

export default memo(ChangePasswordComponent);
